import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';
import { SocketProvider } from './contexts/SocketContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './pages/Login';
import Register from './pages/Register';
import './App.css';
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Vehicles = lazy(() => import('./pages/Vehicles'));
const VehicleNew = lazy(() => import('./pages/VehicleNew'));
const VehicleDetail = lazy(() => import('./pages/VehicleDetail'));
const Devices = lazy(() => import('./pages/Devices'));
const DeviceNew = lazy(() => import('./pages/DeviceNew'));
const DeviceDetail = lazy(() => import('./pages/DeviceDetail'));
const DeviceDataMonitor = lazy(() => import('./pages/DeviceDataMonitor'));
const IgnitionTraining = lazy(() => import('./pages/IgnitionTraining'));
const Locations = lazy(() => import('./pages/Locations'));
const Alarms = lazy(() => import('./pages/Alarms'));
const Geofences = lazy(() => import('./pages/Geofences'));
const RoutesPage = lazy(() => import('./pages/Routes'));
const RouteExport = lazy(() => import('./pages/RouteExport'));
const RouteExportSpeed = lazy(() => import('./pages/RouteExportSpeed'));
const Map = lazy(() => import('./pages/Map'));
const Users = lazy(() => import('./pages/Users'));
const Tenants = lazy(() => import('./pages/Tenants'));
const TenantNew = lazy(() => import('./pages/TenantNew'));
const TenantEdit = lazy(() => import('./pages/TenantEdit'));
const DemoAccounts = lazy(() => import('./pages/DemoAccounts'));
const Reports = lazy(() => import('./pages/Reports'));
const Profile = lazy(() => import('./pages/Profile'));
const SpeedDashboard = lazy(() => import('./pages/SpeedDashboard'));
const SpeedValidation = lazy(() => import('./pages/SpeedValidation'));
const Logs = lazy(() => import('./pages/Logs'));
const AlertsDashboard = lazy(() => import('./pages/AlertsDashboard'));
const Maintenance = lazy(() => import('./pages/Maintenance'));
const MaintenanceNew = lazy(() => import('./pages/MaintenanceNew'));
const MaintenanceTypes = lazy(() => import('./pages/MaintenanceTypes'));
const FuelManagement = lazy(() => import('./pages/FuelManagement'));
const FuelTransactionNew = lazy(() => import('./pages/FuelTransactionNew'));
const Drivers = lazy(() => import('./pages/Drivers'));
const DriverDetail = lazy(() => import('./pages/DriverDetail'));
const DriverNew = lazy(() => import('./pages/DriverNew'));
const DriverEdit = lazy(() => import('./pages/DriverEdit'));
const Analytics = lazy(() => import('./pages/Analytics'));
const RouteOptimization = lazy(() => import('./pages/RouteOptimization'));
const IdleTimeAnalysis = lazy(() => import('./pages/IdleTimeAnalysis'));
const CostAnalysis = lazy(() => import('./pages/CostAnalysis'));
const EfficiencyDashboard = lazy(() => import('./pages/EfficiencyDashboard'));
const AnalyticsNotifications = lazy(() => import('./pages/AnalyticsNotifications'));
const AnalyticsNotificationSettings = lazy(() => import('./pages/AnalyticsNotificationSettings'));
const SpareParts = lazy(() => import('./pages/SpareParts'));
const PartForm = lazy(() => import('./pages/PartForm'));
const PartInventory = lazy(() => import('./pages/PartInventory'));
const PartAuditLog = lazy(() => import('./pages/PartAuditLog'));
const InventoryForm = lazy(() => import('./pages/InventoryForm'));
const MapSettings = lazy(() => import('./pages/MapSettings'));
const InstallerAssistant = lazy(() => import('./pages/InstallerAssistant'));

function AppRoutes() {
  const { isInstallerOnly } = useAuth();
  
  return (
    <Suspense fallback={<div className="loading-container" style={{ minHeight: '60vh' }}><div className="loading-spinner"></div></div>}>
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />

      {/* Protected Routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={
          <Navigate to={isInstallerOnly ? "/installer-assistant" : "/dashboard"} replace />
        } />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="installer-assistant" element={<InstallerAssistant />} />
        <Route path="vehicles" element={<Vehicles />} />
        <Route path="vehicles/new" element={<VehicleNew />} />
        <Route path="vehicles/:id" element={<VehicleDetail />} />
        <Route path="devices" element={<Devices />} />
        <Route path="devices/new" element={<DeviceNew />} />
        <Route path="devices/:id" element={<DeviceDetail />} />
        <Route path="device-monitor" element={<DeviceDataMonitor />} />
        <Route path="ignition-training" element={<IgnitionTraining />} />
        <Route path="locations" element={<Locations />} />
        <Route path="alarms" element={<Alarms />} />
        <Route path="geofences" element={<Geofences />} />
        <Route path="maintenance" element={<Maintenance />} />
        <Route path="maintenance/new" element={<MaintenanceNew />} />
        <Route path="maintenance/types" element={<MaintenanceTypes />} />
        <Route path="fuel-management" element={<FuelManagement />} />
        <Route path="fuel-management/new" element={<FuelTransactionNew />} />
        <Route path="drivers" element={<Drivers />} />
        <Route path="drivers/new" element={<DriverNew />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="analytics/route-optimization" element={<RouteOptimization />} />
        <Route path="analytics/idle-time" element={<IdleTimeAnalysis />} />
        <Route path="analytics/cost-analysis" element={<CostAnalysis />} />
        <Route path="analytics/efficiency" element={<EfficiencyDashboard />} />
        <Route path="analytics/notifications" element={<AnalyticsNotifications />} />
        <Route path="analytics/notifications/settings" element={<AnalyticsNotificationSettings />} />
        <Route path="drivers/:id" element={<DriverDetail />} />
        <Route path="drivers/:id/edit" element={<DriverEdit />} />
        <Route path="routes" element={<RoutesPage />} />
        <Route path="route-export" element={<RouteExport />} />
        <Route path="route-export-speed" element={<RouteExportSpeed />} />
        <Route path="speed-dashboard" element={<SpeedDashboard />} />
        <Route path="speed-validation" element={<SpeedValidation />} />
        <Route path="map" element={<Map />} />
        <Route path="users" element={<Users />} />
        <Route path="tenants" element={<Tenants />} />
        <Route path="tenants/new" element={<TenantNew />} />
        <Route path="tenants/:id/edit" element={<TenantEdit />} />
        <Route path="demo-accounts" element={<DemoAccounts />} />
        <Route path="reports" element={<Reports />} />
        <Route path="logs" element={<Logs />} />
        <Route path="alerts-dashboard" element={<AlertsDashboard />} />
        <Route path="profile" element={<Profile />} />
        <Route path="settings/map" element={<MapSettings />} />
        
        {/* Spare Parts Management */}
        <Route path="inventory/spare-parts" element={<SpareParts />} />
        <Route path="inventory/spare-parts/new" element={<PartForm />} />
        <Route path="inventory/spare-parts/:id/edit" element={<PartForm />} />
        <Route path="inventory/stock" element={<PartInventory />} />
        <Route path="inventory/stock/new" element={<InventoryForm />} />
        <Route path="inventory/audit-log" element={<PartAuditLog />} />
      </Route>

      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <TenantProvider>
          <SocketProvider>
            <div className="App">
              <ErrorBoundary>
                <AppRoutes />
              </ErrorBoundary>
            </div>
          </SocketProvider>
        </TenantProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
